import { useState, useEffect, useCallback } from 'react';
import { 
  getPendingInvitations, 
  createUserInvitation, 
  cancelInvitation, 
  resendInvitation 
} from '../firebase/invitations';
import { sendInvitationEmail, sendInvitationReminder } from '../services/emailService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useUser } from '../contexts/UserContext';
import { canUserManageUsers } from '../firebase/userRoles';

/**
 * Hook personalizado para gerenciar convites de usuários
 * @param {string} salonId - ID do salão
 * @returns {Object} - Funções e dados relacionados a convites
 */
const useInvitations = (salonId) => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { getCurrentRole, hasPermission, currentUser } = useUser();
  
  // Carregar convites pendentes
  const loadInvitations = useCallback(async () => {
    if (!salonId || !currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Verificar permissão para listar convites
      const hasAccess = await canUserManageUsers(currentUser.uid, salonId);
      if (!hasAccess) {
        setInvitations([]);
        return;
      }
      
      const pendingInvitations = await getPendingInvitations(salonId);
      setInvitations(pendingInvitations);
    } catch (err) {
      console.error('Erro ao carregar convites:', err);
      setError('Falha ao carregar convites. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [salonId, currentUser]);
  
  // Efeito para carregar convites ao inicializar
  useEffect(() => {
    loadInvitations();
  }, [loadInvitations]);
  
  // Verificar se o usuário tem permissão para gerenciar convites
  const checkPermission = useCallback(async () => {
    if (!salonId || !currentUser) return false;
    
    // Usar a função canUserManageUsers para verificar permissão
    return canUserManageUsers(currentUser.uid, salonId);
  }, [salonId, currentUser]);
  
  // Criar e enviar um novo convite
  const sendInvite = useCallback(async (email, role, permissions = [], professionalData = null) => {
    if (!salonId || !email) {
      throw new Error('Salão e email são obrigatórios');
    }
    
    // Verificar permissão
    const hasPermission = await checkPermission();
    if (!hasPermission) {
      throw new Error('Você não tem permissão para enviar convites');
    }
    
    try {
      // Buscar dados do salão
      const salonRef = doc(db, 'salons', salonId);
      const salonSnap = await getDoc(salonRef);
      const salonData = salonSnap.exists() ? salonSnap.data() : null;
      
      // Criar convite
      const invitation = await createUserInvitation(
        salonId,
        email,
        role,
        permissions,
        professionalData,
        currentUser?.uid
      );
      
      // Enviar email com o convite
      if (invitation) {
        await sendInvitationEmail(
          invitation,
          salonData,
          currentUser?.displayName || 'Administrador'
        );
      }
      
      // Atualizar lista de convites
      await loadInvitations();
      
      return invitation;
    } catch (err) {
      console.error('Erro ao enviar convite:', err);
      throw err;
    }
  }, [salonId, checkPermission, currentUser, loadInvitations]);
  
  // Cancelar um convite
  const cancelInviteById = useCallback(async (invitationId) => {
    if (!invitationId) {
      throw new Error('ID do convite é obrigatório');
    }
    
    // Verificar permissão
    const hasPermission = await checkPermission();
    if (!hasPermission) {
      throw new Error('Você não tem permissão para cancelar convites');
    }
    
    try {
      const success = await cancelInvitation(invitationId);
      
      if (success) {
        // Atualizar lista localmente para feedback imediato
        setInvitations(prev => prev.filter(inv => inv.id !== invitationId));
      }
      
      return success;
    } catch (err) {
      console.error('Erro ao cancelar convite:', err);
      throw err;
    }
  }, [checkPermission]);
  
  // Reenviar um convite
  const resendInviteById = useCallback(async (invitationId) => {
    if (!invitationId) {
      throw new Error('ID do convite é obrigatório');
    }
    
    // Verificar permissão
    const hasPermission = await checkPermission();
    if (!hasPermission) {
      throw new Error('Você não tem permissão para reenviar convites');
    }
    
    try {
      // Buscar dados do salão
      const salonRef = doc(db, 'salons', salonId);
      const salonSnap = await getDoc(salonRef);
      const salonData = salonSnap.exists() ? salonSnap.data() : null;
      
      // Reenviar convite
      const updatedInvitation = await resendInvitation(invitationId);
      
      // Enviar email de lembrete
      if (updatedInvitation) {
        await sendInvitationReminder(updatedInvitation, salonData);
      }
      
      // Atualizar convite na lista local
      setInvitations(prev => prev.map(inv => 
        inv.id === invitationId ? updatedInvitation : inv
      ));
      
      return updatedInvitation;
    } catch (err) {
      console.error('Erro ao reenviar convite:', err);
      throw err;
    }
  }, [salonId, checkPermission]);
  
  return {
    invitations,
    loading,
    error,
    sendInvite,
    cancelInviteById,
    resendInviteById,
    refreshInvitations: loadInvitations,
    checkPermission
  };
};

export default useInvitations; 